import upperFirst from 'lodash/upperFirst';

import { MapScopedKeyMap } from 'ia-react-core/~/interfaces/MapScopedKeyMap';
import throwError from 'ia-react-core/~/utils/error/#throwError';

export type DataDstProps = {
  name?: string;
  dataDst?: string | ((props: { resourceId: number }) => string);
  dataSrc?: MapScopedKeyMap<{ resourceId: number }>;
  resourceId?: number;
};

export type ValuesDstProps = {
  name?: string;
  valuesDst?: string;
  valuesSrc?: string;
  formId?: number;
};

export type SchemaDecorateComponentOptions ={
  idType?: string;
  prefixType?: string;
  removeCurrentType?: boolean;
};

export interface SchemaOptions {
  strict?: boolean;
  autoFix?: boolean;
}

const CATEGORIES = ['collapsable', 'drawer', 'form', 'modal', 'snackar', 'modal', 'pagination', 'resource', 'table', 'tab', 'workflow', 'page'];

export default abstract class Schema {
  strict: boolean;
  autoFix: boolean;

  constructor({ strict = false, autoFix = false }: SchemaOptions = {}) {
    this.strict = strict;
    this.autoFix = autoFix;
  }

  abstract extendName(type: string, props: Record<string, unknown>): string;

  abstract getValuesDst(props: ValuesDstProps): string;

  abstract getDataDst(props: Partial<DataDstProps>): string;

  abstract decorateComponent<
    Props extends(DataDstProps | ValuesDstProps) & Record<string, unknown>
  >(type: string, props: Props, options?: SchemaDecorateComponentOptions): Props;

  protected CATEGORIES = CATEGORIES;

  protected getName<
  Props extends(DataDstProps | ValuesDstProps) & Record<string, unknown>
>(type: string, props: Props, { idType, prefixType, removeCurrentType = false }: SchemaDecorateComponentOptions) {
    const { name } = props;

    if (this.strict && !name) {
      throwError(`CategorySchema: strict mode requires you to pass a "name" prop on you ${type}.`);
    }

    const defaultedName = name || `${upperFirst(prefixType)}${props[idType]}`;

    const cleanedName = this.removeOtherCategories(defaultedName, removeCurrentType ? null : type);

    return cleanedName;
  }

  protected removeOtherCategories(name: string, type: string) {
    return this.removeCategories(name, type);
  }

  protected appendSuffix(name: string, suffix: string) {
    const prefixRegExp = new RegExp(`^(${CATEGORIES.map(upperFirst).join('|')})`);
    const isGeneratedName = name.match(prefixRegExp);
    if (isGeneratedName) return `${name}${upperFirst(suffix)}`;
    return name;
  }

  protected removeCategories(name = '', ignored: string = null) {
    const cats = CATEGORIES.filter((cat) => cat !== ignored);
    const prefixRegExp = new RegExp(`^((${cats.map((cat) => `${cat}s`).join('|')})\\.)`);
    const suffixRegExp = new RegExp(`(${cats.map(upperFirst).join('|')})$`);
    return name
      .replace(prefixRegExp, '')
      .replace(suffixRegExp, '');
  }
}
